import { onBeforeUnmount, onMounted, watch } from 'vue'
import type { Pinia } from 'pinia'
import { useThemeStore, type ThemeMode } from '@/stores/theme'

/**
 * Composable that wires the theme store to the document root class.
 * Safe to call from main.ts before mount, and from components.
 *
 * When invoked before Pinia has been `app.use(...)`'d, callers may pass
 * the freshly-created Pinia instance explicitly so the store is bound to
 * it (instead of falling back to the default pinia).
 */
export function useTheme(pinia?: Pinia) {
  const store = useThemeStore(pinia)

  function initTheme(): void {
    store.applyResolved()
  }

  function setMode(mode: ThemeMode): void {
    store.setMode(mode)
  }

  function toggle(): void {
    store.cycleTheme()
  }

  // Watch and re-apply (only useful inside components)
  function watchTheme(): void {
    const stop = watch(
      () => store.resolvedTheme,
      () => store.applyResolved(),
    )
    onBeforeUnmount(stop)
  }

  // Track system changes when in auto mode
  function watchSystem(): void {
    let mql: MediaQueryList | null = null
    const handler = (e: MediaQueryListEvent): void => {
      store.setSystemTheme(e.matches ? 'dark' : 'light')
    }
    onMounted(() => {
      if (typeof window === 'undefined' || !window.matchMedia) return
      mql = window.matchMedia('(prefers-color-scheme: dark)')
      mql.addEventListener('change', handler)
    })
    onBeforeUnmount(() => {
      if (mql) mql.removeEventListener('change', handler)
    })
  }

  return {
    store,
    initTheme,
    setMode,
    toggle,
    watchTheme,
    watchSystem,
  }
}
